import React from 'react';
import { motion } from 'framer-motion';
import {
  Plus, Save, RefreshCw, X, Trash2,
  Edit, Check, Download, Upload, Send,
  Sparkles, Shield, Key, Eye, Copy
} from 'lucide-react';

// Base Button Component with simple, professional design
const SimpleButton = ({
  children,
  onClick,
  type = 'button',
  disabled = false,
  loading = false,
  icon: Icon,
  iconPosition = 'left',
  bgColor = 'bg-indigo-600',
  hoverColor = 'hover:bg-indigo-700',
  textColor = 'text-white',
  className = '',
  fullWidth = false,
  size = 'md',
  as = 'button',
  ...props
}) => {
  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-6 py-3 text-lg'
  };

  const baseClasses = `
    ${sizeClasses[size]}
    ${bgColor} ${textColor} ${hoverColor}
    rounded-lg font-medium transition-all duration-200
    flex items-center justify-center whitespace-nowrap
    disabled:opacity-50 disabled:cursor-not-allowed
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `;

  const LoadingSpinner = () => (
    <svg
      className={`animate-spin ${iconPosition === 'left' ? 'mr-2' : 'ml-2'} h-4 w-4`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <>
          {iconPosition === 'left' && <LoadingSpinner />}
          <span>{children}</span>
          {iconPosition === 'right' && <LoadingSpinner />}
        </>
      );
    }

    return (
      <>
        {Icon && iconPosition === 'left' && (
          <Icon className={`w-4 h-4 ${children ? 'mr-2' : ''}`} />
        )}
        {children && <span>{children}</span>}
        {Icon && iconPosition === 'right' && (
          <Icon className={`w-4 h-4 ${children ? 'ml-2' : ''}`} />
        )}
      </>
    );
  };

  const Component = motion[as] || motion.button;

  const componentProps = {
    className: baseClasses,
    whileHover: { scale: disabled || loading ? 1 : 1.02 },
    whileTap: { scale: disabled || loading ? 1 : 0.98 },
    ...props
  };

  // Add button-specific props only for button elements
  if (as === 'button') {
    componentProps.type = type;
    componentProps.onClick = onClick;
    componentProps.disabled = disabled || loading;
  } else if (as === 'label') {
    componentProps.onClick = onClick;
    // For label elements, we want the cursor to be pointer
    componentProps.className = `${baseClasses} cursor-pointer`;
  }

  return (
    <Component {...componentProps}>
      {renderContent()}
    </Component>
  );
};

// Predefined Common Buttons with consistent solid color designs

// Create Button - Indigo (Primary action)
export const CreateButton = ({ children = 'Create', icon = Sparkles, ...props }) => (
  <SimpleButton
    icon={icon}
    bgColor="bg-indigo-600"
    hoverColor="hover:bg-indigo-700"
    {...props}
  >
    {children}
  </SimpleButton>
);

// Save Button - Green (Success action)
export const SaveButton = ({ children = 'Save', icon = Check, ...props }) => (
  <SimpleButton
    icon={icon}
    bgColor="bg-green-600"
    hoverColor="hover:bg-green-700"
    {...props}
  >
    {children}
  </SimpleButton>
);

// Update Button - Blue
export const UpdateButton = ({ children = 'Update', icon = Edit, ...props }) => (
  <SimpleButton
    icon={icon}
    bgColor="bg-blue-600"
    hoverColor="hover:bg-blue-700"
    {...props}
  >
    {children}
  </SimpleButton>
);

// Refresh Button - Cyan (Secondary action)
export const RefreshButton = ({ children = 'Refresh', icon = RefreshCw, ...props }) => (
  <SimpleButton
    icon={icon}
    bgColor="bg-cyan-600"
    hoverColor="hover:bg-cyan-700"
    {...props}
  >
    {children}
  </SimpleButton>
);

// Cancel Button - Gray (Neutral action)
export const CancelButton = ({ children = 'Cancel', icon = X, ...props }) => (
  <SimpleButton
    icon={icon}
    bgColor="bg-gray-600"
    hoverColor="hover:bg-gray-700"
    {...props}
  >
    {children}
  </SimpleButton>
);

// Delete Button - Red (Danger action)
export const DeleteButton = ({ children = 'Delete', icon = Trash2, ...props }) => (
  <SimpleButton
    icon={icon}
    bgColor="bg-red-600"
    hoverColor="hover:bg-red-700"
    {...props}
  >
    {children}
  </SimpleButton>
);

// Additional Common Buttons

// Submit Button - Indigo (same as Create)
export const SubmitButton = ({ children = 'Submit', icon = Send, ...props }) => (
  <SimpleButton
    icon={icon}
    bgColor="bg-indigo-600"
    hoverColor="hover:bg-indigo-700"
    {...props}
  >
    {children}
  </SimpleButton>
);

// View Button - Purple
export const ViewButton = ({ children = 'View', icon = Eye, ...props }) => (
  <SimpleButton
    icon={icon}
    bgColor="bg-purple-600"
    hoverColor="hover:bg-purple-700"
    {...props}
  >
    {children}
  </SimpleButton>
);

// Download Button - Teal
export const DownloadButton = ({ children = 'Download', icon = Download, ...props }) => (
  <SimpleButton
    icon={icon}
    bgColor="bg-teal-600"
    hoverColor="hover:bg-teal-700"
    {...props}
  >
    {children}
  </SimpleButton>
);

// Upload Button - Orange
export const UploadButton = ({ children = 'Upload', icon = Upload, ...props }) => (
  <SimpleButton
    icon={icon}
    bgColor="bg-orange-600"
    hoverColor="hover:bg-orange-700"
    {...props}
  >
    {children}
  </SimpleButton>
);

// Duplicate Button - Violet
export const DuplicateButton = ({ children = 'Duplicate', icon = Copy, ...props }) => (
  <SimpleButton
    icon={icon}
    bgColor="bg-violet-600"
    hoverColor="hover:bg-violet-700"
    {...props}
  >
    {children}
  </SimpleButton>
);

// Permissions Button - Indigo
export const PermissionsButton = ({ children = 'Permissions', icon = Key, ...props }) => (
  <SimpleButton
    icon={icon}
    bgColor="bg-indigo-600"
    hoverColor="hover:bg-indigo-700"
    {...props}
  >
    {children}
  </SimpleButton>
);

// Security Button - Purple
export const SecurityButton = ({ children = 'Security', icon = Shield, ...props }) => (
  <SimpleButton
    icon={icon}
    bgColor="bg-purple-600"
    hoverColor="hover:bg-purple-700"
    {...props}
  >
    {children}
  </SimpleButton>
);

// Export base component for custom colors if needed
export default SimpleButton;
